import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import en from "./locales/en.json";
import es from "./locales/es.json";
import fr from "./locales/fr.json";
import pt from "./locales/pt.json";

export const SUPPORTED_LANGUAGES = ["en", "es", "fr", "pt"] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const LANGUAGE_LABELS: Record<SupportedLanguage, string> = {
  en: "English",
  es: "Español",
  fr: "Français",
  pt: "Português",
};

/**
 * The URL locale is authoritative from the very first paint. Detection
 * (localStorage / navigator) only decides what a bare path redirects to —
 * it must never win over an explicit /fr/* prefix, otherwise the shell
 * renders English strings and repaints once i18next catches up.
 */
function localeFromPath(): SupportedLanguage | undefined {
  if (typeof window === "undefined") return undefined;
  const seg = window.location.pathname.split("/")[1];
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(seg)
    ? (seg as SupportedLanguage)
    : undefined;
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    lng: localeFromPath(),
    // "fr-FR" from navigator detection must resolve to the "fr" bundle.
    load: "languageOnly",
    resources: {
      en: { translation: en },
      es: { translation: es },
      fr: { translation: fr },
      pt: { translation: pt },
    },
    // Explicit chain: every locale falls back to English, never to French.
    fallbackLng: { fr: ["en"], es: ["en"], pt: ["en"], default: ["en"] },
    supportedLngs: SUPPORTED_LANGUAGES as unknown as string[],
    interpolation: { escapeValue: false },
    detection: {
      order: ["localStorage", "htmlTag", "navigator"],
      caches: ["localStorage"],
      lookupLocalStorage: "bp.language",
    },
  });

export default i18n;
